/**
 * Convert a Markdown string into a ProseMirror document conforming to our
 * custom schema. The default `prosemirror-markdown` parser uses the schema
 * shipped by that package — we map its output JSON onto our schema by
 * round-tripping through JSON; nodes our schema doesn't recognise are dropped
 * gracefully (rare for the lesson-note use case which is plain markdown).
 */
import { defaultMarkdownParser } from 'prosemirror-markdown';
import type { Node as ProseNode } from 'prosemirror-model';
import { pmSchema, EMPTY_DOC_JSON } from './pmSchema';

/**
 * Best-effort markdown -> our-schema doc. If the parser blows up (e.g. on a
 * source containing weird tokens), wrap the entire string in a paragraph.
 */
export function markdownToDoc(md: string): Record<string, unknown> {
  if (!md || !md.trim()) return { ...EMPTY_DOC_JSON };
  let parsed: ProseNode | null = null;
  try {
    parsed = defaultMarkdownParser.parse(md);
  } catch {
    parsed = null;
  }
  if (!parsed) return wrapPlainText(md);
  const json = parsed.toJSON() as Record<string, unknown>;
  // Filter the doc through our schema to drop unknown nodes / marks.
  try {
    const doc = pmSchema.nodeFromJSON(json);
    return doc.toJSON() as Record<string, unknown>;
  } catch {
    return wrapPlainText(md);
  }
}

function wrapPlainText(text: string): Record<string, unknown> {
  const lines = text.split('\n');
  return {
    type: 'doc',
    content: lines.map((line) => (line.length
      ? { type: 'paragraph', content: [{ type: 'text', text: line }] }
      : { type: 'paragraph' })),
  };
}
